import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "../components/layout";
import { Alert, AlertDescription, Button, Card, CardContent, CardHeader } from "@/components/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { 
  Save, 
  Eye, 
  AlertCircle,
  CheckCircle,
  User,
  RotateCcw,
  ArrowLeft,
  Upload
} from "lucide-react";
import { getHelpCenter, getItem, updateItem, getHelpCenterStructure, importDocument } from "../services/api";
import type { UpdateItemRequest } from "../types";
import type { Article } from "../types";
import { useUIStore } from "../store/uiStore";
import { IconPicker } from "@/components/features/help-center/IconPicker";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "react-hot-toast";
import { TipTapEditor } from "@/components/editor/TipTapEditor";

const EditArticlePage: React.FC = () => {
  const { itemId, helpCenterId: helpCenterIdParam } = useParams<{ itemId: string; helpCenterId: string }>();
  const navigate = useNavigate();
  const { setIsSaving, lockNavigation, unlockNavigation } = useUIStore();
  

  
  const [article, setArticle] = useState<Article | null>(null);
  const [item, setItem] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [helpCenterInfo, setHelpCenterInfo] = useState<{ id: number; title: string; slug?: string } | null>(null);
  const [helpCenterStructure, setHelpCenterStructure] = useState<any[]>([]);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [contentHtml, setContentHtml] = useState<string>('');
  const [contentText, setContentText] = useState('');
  const [icon, setIcon] = useState('');
  const [showAuthor, setShowAuthor] = useState(true);
  const [isPublished, setIsPublished] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Navigation lock state
  const initialFormState = useRef<{
    title: string;
    description: string;
    slug: string;
    contentText: string;
    contentHtml: string;
    icon: string;
    showAuthor: boolean;
    isPublished: boolean;
  } | null>(null);

    // Help center ID is required - items always belong to a help center
  if (!helpCenterIdParam || !itemId) {
    return <div>Missing required parameters</div>;
  }
  
  // Build breadcrumbs
  const breadcrumbs = (() => {
    const crumbs = [
      { title: 'Dashboard', href: 'dashboard' },
      { title: 'Help Centers', href: 'help-centers' },
    ] as any;
    
    if (helpCenterInfo) {
      crumbs.push(
        { title: helpCenterInfo.title, href: `help-centers/${helpCenterInfo.id}/structure` },
        { title: 'Edit Article', isCurrentPage: true }
      );
    } else {
      crumbs.push({ title: 'Edit Article', isCurrentPage: true });
    }
    
    return crumbs;
  })();

  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Check if form has been modified
  const checkForChanges = () => {
    if (!initialFormState.current) return false;
    
    const current = {
      title,
      description,
      slug,
      contentText,
      contentHtml,
      icon,
      showAuthor,
      isPublished
    };
    
    return JSON.stringify(current) !== JSON.stringify(initialFormState.current);
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    if (!initialFormState.current) return;
    
    if (!confirm('Are you sure you want to discard all changes? This action cannot be undone.')) {
      return;
    }

    // Reset to initial state
    setTitle(initialFormState.current.title);
    setDescription(initialFormState.current.description);
    setSlug(initialFormState.current.slug);
    setSlugManuallyEdited(true); // Preserve manual edit state when discarding
    setContentText(initialFormState.current.contentText);
    setContentHtml(initialFormState.current.contentHtml);
    setIcon(initialFormState.current.icon);
    setShowAuthor(initialFormState.current.showAuthor);
    setIsPublished(initialFormState.current.isPublished);
    
    // If we have the article data, reset content too
    if (article && article.content) {
      setContent(article.content);
    } else {
      setContent(null);
    }
    
    toast.success('Changes discarded');
  };

  // Update navigation lock based on changes
  useEffect(() => {
    // Don't lock navigation until initial state is set (data has loaded)
    if (!initialFormState.current) {
      unlockNavigation();
      return;
    }
    
    const hasChanges = checkForChanges();
    
    if (hasChanges) {
      lockNavigation("You have unsaved changes in your article");
    } else {
      unlockNavigation();
    }
  }, [title, description, slug, contentText, contentHtml, icon, showAuthor, isPublished, lockNavigation, unlockNavigation]);

  // Handle browser beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show warning if initial state is set and there are actual changes
      if (initialFormState.current && checkForChanges()) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [title, description, slug, contentText, contentHtml, icon, showAuthor, isPublished]);

  // Fetch help center info and item data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch help center info
        const helpCenterResponse = await getHelpCenter(helpCenterIdParam);
        if (helpCenterResponse.ok && helpCenterResponse.helpCenter) {
          const helpCenter = helpCenterResponse.helpCenter;
          setHelpCenterInfo({
            id: helpCenter.id,
            title: helpCenter.title,
            slug: helpCenter.slug,
          });
          
          // Fetch help center structure for reference blocks
          const structureResponse = await getHelpCenterStructure(helpCenterIdParam);
          if (structureResponse.ok && structureResponse.structure) {
            setHelpCenterStructure(structureResponse.structure);
          }
        }

        // Fetch item data (which includes article or guide)
        const itemResponse = await getItem(helpCenterIdParam, itemId);
        if (itemResponse.ok && itemResponse.item) {
          const itemData = itemResponse.item;

          // Redirect to guide editor if this is a guide
          if (itemData.item_type === 'guide') {
            navigate(`/help-centers/${helpCenterIdParam}/items/${itemId}/edit-guide`, { replace: true });
            return;
          }

          setItem(itemData);

          if (itemData.item_type === 'article' && itemData.article) {
            const articleData = itemData.article;
            setArticle(articleData);
            setTitle(articleData.title);
            setDescription(articleData.description || '');
            setSlug(articleData.slug || '');
            setSlugManuallyEdited(true);
            setContent(articleData.content);
            setContentHtml(articleData.content_html || '');
            setContentText(articleData.content_text || '');
            setIcon(articleData.icon || '');
            setShowAuthor(articleData.settings?.showAuthor ?? true);
            setIsPublished(itemData.is_published); // Use item-level visibility

            initialFormState.current = {
              title: articleData.title,
              description: articleData.description || '',
              slug: articleData.slug || '',
              contentText: articleData.content_text || '',
              contentHtml: articleData.content_html || '',
              icon: articleData.icon || '',
              showAuthor: articleData.settings?.showAuthor ?? true,
              isPublished: itemData.is_published
            };
          }
        } else {
          setError(itemResponse.error || 'Failed to fetch item');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
        // Ensure navigation is unlocked after data loads (or fails to load)
        unlockNavigation();
      }
    };

    fetchData();
  }, [helpCenterIdParam, itemId, unlockNavigation, navigate]);


  const handleSave = async () => {
    if (!item || !itemId) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const trimmedSlug = slug.trim();
      const originalSlug = article?.slug || '';
      
      // Always update through item endpoint
      const updateData: UpdateItemRequest = {
        is_published: isPublished,
        article_title: title,
        article_description: description,
        article_content: content,
        article_content_html: contentHtml,
        article_content_text: contentText,
        article_icon: icon || null,
        article_settings: { showAuthor: showAuthor },
        // Only send slug if it was explicitly changed
        ...(trimmedSlug !== originalSlug && trimmedSlug ? { article_slug: trimmedSlug } : {}),
      };

      const response = await updateItem(helpCenterIdParam, itemId, updateData);
      
      if (!response.ok) {
        throw new Error(response.error || 'Failed to update item');
      }

      // Update local state with response
      if (response.item) {
        setItem(response.item);
        if (response.item.article) {
          setArticle(response.item.article);
        }
      }

      // Update initial state after successful save
      initialFormState.current = {
        title,
        description,
        slug: trimmedSlug || originalSlug,
        contentText,
        contentHtml,
        icon,
        showAuthor,
        isPublished
      };

      // Unlock navigation after successful save
      unlockNavigation();

      toast.success('Item saved successfully!');
      
      // Navigate back to help center structure
      if (helpCenterInfo) {
        navigate(`/help-centers/${helpCenterInfo.id}/structure`);
      }
      
    } catch (err) {
      console.error('Error saving:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle document import
  const handleImportDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedExtensions = ['docx', 'html', 'md', 'markdown', 'txt'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      toast.error(`Unsupported file format. Allowed formats: ${allowedExtensions.join(', ')}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsImporting(true);
    try {
      const response = await importDocument(file);
      
      if (response.ok && response.content) {
        // Update editor content with imported content
        // The TipTapEditor's useEffect will detect the content change and update the editor
        setContent(response.content);
        
        // If frontmatter metadata was extracted, populate article fields
        if (response.metadata) {
          if (response.metadata.title) {
            setTitle(response.metadata.title);
            // Auto-generate slug from title if slug wasn't provided in frontmatter
            if (!response.metadata.slug && !slugManuallyEdited) {
              setSlug(generateSlug(response.metadata.title));
            }
          }
          if (response.metadata.description) {
            setDescription(response.metadata.description);
          }
          if (response.metadata.icon) {
            setIcon(response.metadata.icon);
          }
          if (response.metadata.slug) {
            setSlug(response.metadata.slug);
            setSlugManuallyEdited(true);
          }
          // Note: tags are not directly settable in the current UI, but could be added later
        }
        
        toast.success('Document imported successfully! Review and edit as needed.');
      } else {
        toast.error(response.error || 'Failed to import document');
      }
    } catch (error) {
      console.error('Error importing document:', error);
      toast.error('Failed to import document');
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Calculate word and character count
  const wordCount = contentText.trim() ? contentText.trim().split(/\s+/).length : 0;
  const characterCount = contentText.length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200)); // Assuming 200 words per minute

  if (loading) {
  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-8 space-y-6">
        {/* Help Center Context Banner */}
        {/* {helpCenterInfo && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  This article will be added to "{helpCenterInfo.title}"
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                  {helpCenterInfo.categoryId 
                    ? 'It will be placed in the selected category'
                    : 'It will be added to the help center structure'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={() => {
                // Remove help center context from URL
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('helpCenterId');
                newParams.delete('categoryId');
                navigate(`/articles/${articleId}/edit${newParams.toString() ? `?${newParams.toString()}` : ''}`, { replace: true });
                setHelpCenterInfo(null);
              }}
              title="Remove help center context"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )} */}
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const handleBack = () => {
    if (helpCenterInfo) {
      navigate(`/help-centers/${helpCenterInfo.id}/structure`);
    } else {
      navigate('/help-centers');
    }
  };

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-4">
        {/* Back Button */}
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {helpCenterInfo ? helpCenterInfo.title : 'Help Centers'}
          </Button>
        </div>

        
        {/* Status Messages */}
        {error && (
          <Card className="border-destructive mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {success && (
          <Card className="border-green-200 bg-green-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" />
                <span>{success}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Article Metadata Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center">
                    <IconPicker 
                      value={icon || null} 
                      onChange={(newIcon) => setIcon(newIcon || '')} 
                    />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <Input
                        value={title}
                        onChange={(e) => {
                          const newTitle = e.target.value;
                          setTitle(newTitle);
                          // Only auto-generate slug if user hasn't manually edited it
                          if (!slugManuallyEdited) {
                            setSlug(generateSlug(newTitle));
                          }
                        }}
                        placeholder="Enter your article title..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="slug" className="text-sm text-muted-foreground mb-1 block">
                        Slug
                      </Label>
                      <Input
                        id="slug"
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value);
                          // Mark slug as manually edited when user types in it
                          setSlugManuallyEdited(true);
                        }}
                        placeholder="article-slug"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        URL-friendly identifier. Auto-generated from title if left empty.
                      </p>
                    </div>
                    <div>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Write a short description..."
                        className="h-24"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Article Editor Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Alert variant="info" className="flex-1">
                    <AlertDescription>
                      You can either write your article directly or press / to open the command palette.
                    </AlertDescription>
                  </Alert>
                  <div className="ml-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx,.html,.md,.markdown,.txt"
                      onChange={handleImportDocument}
                      className="hidden"
                      disabled={isImporting}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isImporting}
                      className="gap-2"
                    >
                      {isImporting ? (
                        <>
                          <RotateCcw className="h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Import Document
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <TipTapEditor
                  helpCenterStructure={helpCenterStructure}
                  helpCenterSlug={helpCenterInfo?.slug}
                  content={content || {
                    type: 'doc',
                    content: [
                      {
                        type: 'paragraph',
                      },
                    ],
                  }}
                  onChange={(json, html, text) => {
                    setContent(json);
                    setContentHtml(html);
                    setContentText(text);
                  }}
                  placeholder="Start writing your article or press / to open the command palette"
                  editable={true}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Settings */}
          <div className="space-y-6">
            {/* Article Settings */}
            <Card>
              <CardHeader className="pb-0">
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Publication Status */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      <Label className="text-sm font-medium">Publish</Label>
                    </div>
                    <Switch
                      checked={isPublished}
                      onCheckedChange={setIsPublished}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Show the article to the public
                  </span>
                </div>

                <Separator />

                {/* Author Display */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <Label className="text-sm font-medium">Show Author</Label>
                    </div>
                    <Switch
                      checked={showAuthor}
                      onCheckedChange={setShowAuthor}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Display author information with the article
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <Button 
                  onClick={handleSave}
                  className="w-full"
                  size="lg"
                  disabled={!checkForChanges()}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
                <Button 
                  onClick={handleDiscardChanges}
                  variant="outline"
                  className="w-full"
                  size="lg"
                  disabled={!checkForChanges()}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Discard Changes
                </Button>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Words</span>
                    <span className="text-sm font-medium">{wordCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Characters</span>
                    <span className="text-sm font-medium">{characterCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Reading time</span>
                    <span className="text-sm font-medium">{readingTime} min</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default EditArticlePage;